import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import JqxGrid, { jqx } from 'jqwidgets-scripts/jqwidgets-react-tsx/jqxgrid';
import 'jqwidgets-scripts/jqwidgets/styles/jqx.light.css';
import {
    DefaultButton, Dialog, DialogFooter, DialogType, Dropdown, PrimaryButton, Spinner, SpinnerSize,
} from '@fluentui/react';
import MessageBox from '../../../components/Common/Message/MessageBox';
import MainAppContext from '../../../MainAppContext';
import { __MessageTypes } from '../../../common/Dictionaries';
import { getGridLocalization, isNullUndefinedOrEmpty, showMessage } from '../../../common/Helpers';
import { applyFilters, handlerFilter, removeGridLocalStorageFilters, setterActionFired, useFilterActionComponentData } from '../../../common/Helpers/JqxGridFilter';
import {
    getListObligationInconsistencies,
    previewObligationInconsistencyAction,
    applyObligationInconsistencyAction,
} from '../../../services/sCO/obligation.service';

// Tipos de problema y acciones: deben coincidir con ENUM_TIPO_INCONGRUENCIA_OBLIGACION y ENUM_ACCION_INCONGRUENCIA_OBLIGACION del backend.
const TYPE_OUT_OF_AREA = 1;
const TYPE_IMPEDIR_NOT_EP = 2;

const ACTIONS = [
    { id: 1, typeId: TYPE_OUT_OF_AREA,     textKey: 'data-lang-lbl-inconsistency-action-addarea' },
    { id: 2, typeId: TYPE_OUT_OF_AREA,     textKey: 'data-lang-lbl-inconsistency-action-deactivatelink' },
    { id: 3, typeId: TYPE_IMPEDIR_NOT_EP,  textKey: 'data-lang-lbl-inconsistency-action-impedirno' },
    { id: 4, typeId: TYPE_IMPEDIR_NOT_EP,  textKey: 'data-lang-lbl-inconsistency-action-markcategoryep' },
];

const ACTION_DEACTIVATE_LINK = 2;
const ACTION_MARK_CATEGORY_EP = 4;

const FILTER_ALL = 'all';
const MAX_PREVIEW_ROWS = 100;
// Nombre con el que JqxGridFilter guarda los filtros de la grilla (se re-aplican cuando la grilla se vuelve a enlazar).
const GRID_FILTER_NAME = 'ObligationInconsistency';

const GRID_DATA_FIELDS = [
    { name: 'key',                  type: 'string' },
    { name: 'selection',            type: 'bool' },
    { name: 'tipoTexto',            type: 'string' },
    { name: 'obligacionNombre',     type: 'string' },
    { name: 'categoriaNombre',      type: 'string' },
    { name: 'contratoTexto',        type: 'string' },
    { name: 'legalEntityBuyerName', type: 'string' },
    { name: 'detalleTexto',         type: 'string' },
    { name: 'impactoTexto',         type: 'string' },
];

export default function ObligationInconsistency() {
    const { t, i18n } = useTranslation();
    const contextSite = useContext(MainAppContext);
    // La pantalla es solo para Super Administrador (sAdmin): el menú, la ruta (Router.js) y el API lo restringen; esto es la última barrera.
    const canFix = contextSite.securityProps.isSAdmin === true;

    const gridRef = useRef();
    // Filas marcadas (por key). Vive fuera de la grilla para no perder la selección al cambiar el filtro de tipo.
    const selectedKeysRef = useRef(new Set());

    const [messageConfig, setMessageConfig] = useState({ message: null, messageType: "", showMessage: false });
    const [list, setList] = useState(null);
    const [reloadCounter, setReloadCounter] = useState(0);
    const [selectionVersion, setSelectionVersion] = useState(0);
    const [typeFilter, setTypeFilter] = useState(FILTER_ALL);
    const [selectedCount, setSelectedCount] = useState(0);
    const [working, setWorking] = useState(false);

    // Vista previa pendiente de confirmar: { action, items, results }
    const [pendingAction, setPendingAction] = useState(null);

    // El servidor responde con una clave de idioma; si esa clave no está traducida se muestra el error genérico en vez de la clave cruda.
    const tServerMessage = (message) => (!isNullUndefinedOrEmpty(message) && i18n.exists(message)) ? t(message) : t('data-lang-msg-operationerror');

    useFilterActionComponentData(gridRef, messageConfig, GRID_FILTER_NAME);

    // Una visita nueva a la pantalla parte sin filtros guardados.
    useEffect(() => {
        removeGridLocalStorageFilters(GRID_FILTER_NAME);
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadList = async () => {
            try {
                const serviceResponse = await getListObligationInconsistencies();
                if (cancelled) return;

                if (serviceResponse.data.succeeded) {
                    setList(serviceResponse.data.data);
                }
                else {
                    setList([]);
                    showMessage(setMessageConfig, true, __MessageTypes.ERROR, tServerMessage(serviceResponse.data.message));
                }
            }
            catch (error) {
                if (cancelled) return;
                setList([]);
                showMessage(setMessageConfig, true, __MessageTypes.ERROR, t('data-lang-msg-operationerror'));
            }
        };

        loadList();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadCounter]);

    const typeName = (typeId) => t(typeId === TYPE_OUT_OF_AREA ? 'data-lang-lbl-inconsistency-type-outofarea' : 'data-lang-lbl-inconsistency-type-impedirnotep');

    const contractText = (item) => item.tipoId === TYPE_OUT_OF_AREA
        ? [item.codContrato, item.contratoNombre].filter(x => !isNullUndefinedOrEmpty(x)).join(' - ')
        : '';

    const detailText = (item) => {
        if (item.tipoId === TYPE_OUT_OF_AREA) {
            if (isNullUndefinedOrEmpty(item.legalEntityBuyerId)) {
                return t('data-lang-lbl-inconsistency-detail-nobuyer');
            }
            if (!item.obligacionTieneAreas) {
                return t('data-lang-lbl-inconsistency-detail-noareas');
            }
            return `${t('data-lang-lbl-inconsistency-detail-outofarea')} ${item.legalEntityBuyerName ?? ''}`.trim();
        }
        return t('data-lang-lbl-inconsistency-detail-impedirnotep');
    };

    const impactText = (item) => item.tipoId === TYPE_OUT_OF_AREA
        ? `${item.cumplimientosPendientes} ${t('data-lang-lbl-inconsistency-impact-pending')}`
        : `${item.vinculosActivos} ${t('data-lang-lbl-inconsistency-impact-links')}`;

    // Texto ya resuelto para mostrar y exportar. Los valores nulos pasan a '' para que el filtro de la grilla no falle.
    const rows = useMemo(() => (list || []).map(item => ({
        ...item,
        legalEntityBuyerName: item.legalEntityBuyerName ?? '',
        tipoTexto: typeName(item.tipoId),
        contratoTexto: contractText(item),
        detalleTexto: detailText(item),
        impactoTexto: impactText(item),
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list, i18n.language]);

    const countOutOfArea = rows.filter(x => x.tipoId === TYPE_OUT_OF_AREA).length;
    const countImpedirNotEp = rows.filter(x => x.tipoId === TYPE_IMPEDIR_NOT_EP).length;

    // Filas que alimentan la grilla: las del tipo elegido, con la casilla según la selección guardada.
    const gridRows = useMemo(() => rows
        .filter(item => typeFilter === FILTER_ALL || item.tipoId === typeFilter)
        .map(item => ({ ...item, selection: selectedKeysRef.current.has(item.key) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, typeFilter, selectionVersion]);

    // El adaptador se memoriza: así los cambios de estado (contador, diálogos) no vuelven a enlazar la grilla.
    const source = useMemo(() => new jqx.dataAdapter({
        localData: gridRows,
        dataType: 'array',
        dataFields: GRID_DATA_FIELDS,
        id: 'key',
    }), [gridRows]);

    // Filas que la grilla muestra hoy (con sus filtros de columna aplicados).
    const getVisibleKeys = () => new Set((gridRef.current?.getrows?.() || []).map(row => row.key));

    // Marcadas Y visibles: es lo que ve el usuario y sobre lo que actúan los botones.
    const getSelectedItems = () => {
        const visibleKeys = getVisibleKeys();
        return gridRows.filter(item => selectedKeysRef.current.has(item.key) && visibleKeys.has(item.key));
    };

    const refreshSelectedCount = () => setSelectedCount(getSelectedItems().length);

    // Tras volver a enlazar la grilla: se re-aplican los filtros de columna guardados y se recalcula el contador.
    useEffect(() => {
        try {
            if (window.localStorage.getItem(`grid_filters_${GRID_FILTER_NAME}`)) {
                applyFilters(gridRef, GRID_FILTER_NAME, true);
            }
        }
        catch (error) {
            // Sin acceso a localStorage la grilla simplemente queda sin filtros.
        }
        refreshSelectedCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source]);

    const onCellvaluechanged = (e) => {
        if (e.args.datafield !== 'selection') return;

        // rowindex es el índice del registro en la fuente (no depende del orden ni de los filtros de la grilla).
        const row = gridRef.current?.getrowdata?.(e.args.rowindex) ?? gridRows[e.args.rowindex];
        if (!row) return;

        if (e.args.value === true) {
            selectedKeysRef.current.add(row.key);
        }
        else {
            selectedKeysRef.current.delete(row.key);
        }
        refreshSelectedCount();
    };

    const onSelectVisible = () => {
        getVisibleKeys().forEach(key => selectedKeysRef.current.add(key));
        setSelectionVersion(prev => prev + 1);
    };

    const onClearSelection = () => {
        selectedKeysRef.current.clear();
        setSelectionVersion(prev => prev + 1);
    };

    const reloadList = () => {
        setterActionFired(GRID_FILTER_NAME, 1);
        setList(null);
        setReloadCounter(prev => prev + 1);
    };

    // La traducción de la grilla se memoriza para que un cambio de estado no la vuelva a aplicar.
    const localization = useMemo(() => getGridLocalization(t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]);

    const columns = useMemo(() => [
        { text: t('data-lang-lbl-select'),                         dataField: 'selection',            width: 80,  pinned: true, columntype: 'checkbox', editable: true, filterable: false, sortable: false },
        { text: t('data-lang-lbl-inconsistency-col-type'),         dataField: 'tipoTexto',            width: 230, editable: false },
        { text: t('data-lang-lbl-inconsistency-col-obligation'),   dataField: 'obligacionNombre',     width: 300, editable: false },
        { text: t('data-lang-lbl-inconsistency-col-category'),     dataField: 'categoriaNombre',      width: 180, editable: false },
        { text: t('data-lang-lbl-inconsistency-col-contract'),     dataField: 'contratoTexto',        width: 280, editable: false },
        { text: t('data-lang-lbl-inconsistency-col-buyer'),        dataField: 'legalEntityBuyerName', width: 200, editable: false },
        { text: t('data-lang-lbl-inconsistency-col-detail'),       dataField: 'detalleTexto',         width: 380, editable: false },
        { text: t('data-lang-lbl-inconsistency-col-impact'),       dataField: 'impactoTexto',         width: 190, editable: false },
    ].filter(column => canFix || column.dataField !== 'selection'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language, canFix]);

    // Exporta lo que se ve: el filtro de tipo y los filtros de columna de la grilla.
    const handlerExport = () => {
        const visibleKeys = getVisibleKeys();

        const exportRows = gridRows.filter(item => visibleKeys.has(item.key)).map(item => ({
            [t('data-lang-lbl-inconsistency-col-type')]: item.tipoTexto,
            [t('data-lang-lbl-inconsistency-col-obligation')]: item.obligacionNombre,
            [t('data-lang-lbl-inconsistency-col-category')]: item.categoriaNombre,
            [t('data-lang-lbl-inconsistency-col-contractcode')]: item.codContrato ?? '',
            [t('data-lang-lbl-inconsistency-col-contract')]: item.contratoNombre ?? '',
            [t('data-lang-lbl-inconsistency-col-buyer')]: item.legalEntityBuyerName ?? '',
            [t('data-lang-lbl-inconsistency-col-detail')]: item.detalleTexto,
            [t('data-lang-lbl-inconsistency-col-impact')]: item.impactoTexto,
        }));

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(exportRows), 'Data');
        XLSX.writeFile(workbook, 'ObligationInconsistencies_ExportedData.xlsx');
    };

    const buildActionPayload = (action, items) => ({
        accionId: action.id,
        items: items.map(item => ({ tipoId: item.tipoId, obligacionId: item.obligacionId, elementId: item.elementId ?? null })),
    });

    // Paso 1: vista previa. No cambia nada; muestra qué pasaría con cada fila seleccionada del tipo que corresponde a la acción.
    const onStartAction = async (action) => {
        const items = getSelectedItems().filter(item => item.tipoId === action.typeId);

        if (items.length === 0) {
            showMessage(setMessageConfig, true, __MessageTypes.WARNING, t('data-lang-msg-inconsistency-selectrows'));
            return;
        }

        setWorking(true);
        try {
            const serviceResponse = await previewObligationInconsistencyAction(buildActionPayload(action, items));
            if (serviceResponse.data.succeeded) {
                setPendingAction({ action, items, results: serviceResponse.data.data });
            }
            else {
                showMessage(setMessageConfig, true, __MessageTypes.ERROR, tServerMessage(serviceResponse.data.message));
            }
        }
        catch (error) {
            showMessage(setMessageConfig, true, __MessageTypes.ERROR, t('data-lang-msg-operationerror'));
        }
        setWorking(false);
    };

    // Paso 2: aplicar lo que se vio en la vista previa y recargar la lista.
    const onConfirmAction = async () => {
        const { action, items } = pendingAction;
        setPendingAction(null);
        setWorking(true);

        try {
            const serviceResponse = await applyObligationInconsistencyAction(buildActionPayload(action, items));
            if (serviceResponse.data.succeeded) {
                const results = serviceResponse.data.data;
                const applied = results.filter(x => x.aplicado).length;
                const failed = results.filter(x => !x.aplicado && !x.aplicable && x.mensaje === 'data-lang-msg-operationerror').length;
                const failedText = failed > 0 ? `. ${t('data-lang-msg-inconsistency-failedcount')}: ${failed}` : '';

                showMessage(setMessageConfig, true, failed > 0 ? __MessageTypes.WARNING : __MessageTypes.SUCCESS,
                    `${t('data-lang-msg-inconsistency-appliedcount')}: ${applied} / ${results.length}${failedText}`);
            }
            else {
                showMessage(setMessageConfig, true, __MessageTypes.ERROR, tServerMessage(serviceResponse.data.message));
            }
        }
        catch (error) {
            showMessage(setMessageConfig, true, __MessageTypes.ERROR, t('data-lang-msg-operationerror'));
        }

        selectedKeysRef.current.clear();
        reloadList();
        setWorking(false);
    };

    const renderPreviewDialog = () => {
        if (pendingAction === null) return null;

        const { action, items, results } = pendingAction;
        const applicable = results.filter(x => x.aplicable);
        const notApplicable = results.filter(x => !x.aplicable);
        const itemsByKey = items.reduce((acc, item) => ({ ...acc, [item.key]: item }), {});

        const pendingToDelete = applicable.reduce((sum, x) => sum + (x.cumplimientosAEliminar || 0), 0);
        // Marcar la categoría cambia a todas sus obligaciones: se suma una sola vez por categoría.
        const affectedByCategory = {};
        applicable.forEach(x => {
            const item = itemsByKey[x.key];
            if (item) affectedByCategory[item.categoriaId] = x.obligacionesAfectadas || 0;
        });
        const affectedObligations = Object.values(affectedByCategory).reduce((sum, n) => sum + n, 0);

        return (
            <Dialog
                hidden={false}
                onDismiss={() => setPendingAction(null)}
                dialogContentProps={{ type: DialogType.normal, title: t(action.textKey) }}
                minWidth={640}
                modalProps={{ isBlocking: true }}
            >
                <div>
                    <p><b>{applicable.length}</b> {t('data-lang-msg-inconsistency-preview-applicable')}</p>
                    {notApplicable.length > 0 && <p><b>{notApplicable.length}</b> {t('data-lang-msg-inconsistency-preview-notapplicable')}</p>}
                    {action.id === ACTION_DEACTIVATE_LINK && applicable.length > 0 &&
                        <p>{t('data-lang-msg-inconsistency-preview-deletepending')}: <b>{pendingToDelete}</b>. {t('data-lang-msg-inconsistency-preview-irreversible')}</p>}
                    {action.id === ACTION_MARK_CATEGORY_EP && applicable.length > 0 &&
                        <p>{t('data-lang-msg-inconsistency-preview-categoryobligations')}: <b>{affectedObligations}</b>.</p>}
                    <div style={{ maxHeight: 260, overflow: 'auto' }}>
                        <table className='w-100' style={{ fontSize: 12 }}>
                            <thead>
                                <tr>
                                    <th>{t('data-lang-lbl-inconsistency-col-obligation')}</th>
                                    <th>{t('data-lang-lbl-inconsistency-col-contract')}</th>
                                    <th>{t('data-lang-lbl-inconsistency-col-result')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.slice(0, MAX_PREVIEW_ROWS).map(result => {
                                    const item = itemsByKey[result.key];
                                    return (
                                        <tr key={result.key}>
                                            <td>{item?.obligacionNombre}</td>
                                            <td>{item?.contratoTexto}</td>
                                            <td>{tServerMessage(result.mensaje)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {results.length > MAX_PREVIEW_ROWS && <p className='text-muted'>{`+ ${results.length - MAX_PREVIEW_ROWS}`}</p>}
                    </div>
                </div>
                <DialogFooter>
                    <PrimaryButton onClick={onConfirmAction} text={t('data-lang-lbl-inconsistency-apply')} disabled={applicable.length === 0} />
                    <DefaultButton onClick={() => setPendingAction(null)} text={t('data-lang-btn-close')} />
                </DialogFooter>
            </Dialog>
        );
    };

    return (
        <div className="col-12 listWithActions h-100">
            <Card className="h-100">
                <Card.Header>
                    <Card.Title className="d-flex justify-content-between">
                        <h4><b>{t('data-lang-maintainer-title-obligationinconsistency')}</b></h4>
                    </Card.Title>
                </Card.Header>
                <Card.Footer>
                    <MessageBox
                        inputParam_Message={messageConfig.message}
                        inputParam_Display={messageConfig.showMessage}
                        inputParam_MessageType={messageConfig.messageType}
                        inputParam_SetterMessage={setMessageConfig}
                    />
                </Card.Footer>
                <Card.Body style={{ width: "100%" }} className="h-100">
                    <p className="mb-2">{t('data-lang-msg-inconsistency-intro')}</p>
                    {list === null
                        ? <Spinner size={SpinnerSize.large} />
                        : <>
                            <div className="d-flex flex-wrap align-items-end mb-2" style={{ gap: 12 }}>
                                <Dropdown
                                    label={t('data-lang-lbl-inconsistency-filter-type')}
                                    selectedKey={typeFilter}
                                    onChange={(ev, option) => setTypeFilter(option.key)}
                                    options={[
                                        { key: FILTER_ALL, text: `${t('data-lang-lbl-inconsistency-filter-all')} (${rows.length})` },
                                        { key: TYPE_OUT_OF_AREA, text: `${t('data-lang-lbl-inconsistency-type-outofarea')} (${countOutOfArea})` },
                                        { key: TYPE_IMPEDIR_NOT_EP, text: `${t('data-lang-lbl-inconsistency-type-impedirnotep')} (${countImpedirNotEp})` },
                                    ]}
                                    styles={{ root: { minWidth: 320 } }}
                                />
                                <DefaultButton iconProps={{ iconName: 'Download' }} text={t('data-lang-btn-exportdata')} onClick={handlerExport} disabled={gridRows.length === 0} />
                                <DefaultButton iconProps={{ iconName: 'Refresh' }} text={t('data-lang-btn-refresh')} onClick={reloadList} />
                            </div>
                            {canFix &&
                                <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: 8 }}>
                                    <span>{t('data-lang-lbl-inconsistency-selected')}: <b>{selectedCount}</b></span>
                                    <DefaultButton text={t('data-lang-lbl-inconsistency-selectvisible')} onClick={onSelectVisible} disabled={working || gridRows.length === 0} />
                                    <DefaultButton text={t('data-lang-lbl-inconsistency-clearselection')} onClick={onClearSelection} disabled={working || selectedCount === 0} />
                                    {ACTIONS.map(action => (
                                        <DefaultButton
                                            key={action.id}
                                            text={t(action.textKey)}
                                            disabled={working || selectedCount === 0}
                                            onClick={() => onStartAction(action)}
                                        />
                                    ))}
                                    {working && <Spinner size={SpinnerSize.small} />}
                                </div>
                            }
                            {gridRows.length === 0
                                ? <p><b>{t('data-lang-msg-inconsistency-empty')}</b></p>
                                : <JqxGrid
                                    ref={gridRef}
                                    altrows={true}
                                    enabletooltips={true}
                                    width={'100%'}
                                    theme={'light'}
                                    height={'500px'}
                                    editable={canFix}
                                    sortable={true}
                                    filterable={true}
                                    showfilterrow={true}
                                    localization={localization}
                                    source={source}
                                    columns={columns}
                                    onCellvaluechanged={onCellvaluechanged}
                                    onFilter={(event) => { handlerFilter(event, GRID_FILTER_NAME); refreshSelectedCount(); }}
                                />
                            }
                        </>
                    }
                    {renderPreviewDialog()}
                </Card.Body>
            </Card>
        </div>
    );
}
